import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Trash2, Loader2 } from "lucide-react";

/**
 * Themed confirm-delete dialog (light + dark + mobile responsive).
 * Replaces the ugly browser `window.confirm`.
 *
 * Props:
 *   - open, onCancel(), onConfirm()
 *   - title: "Delete this module?"
 *   - itemLabel: e.g. "DELETEME UITest" (rendered bold inside the body)
 *   - description: small print under the item label
 *   - busy: shows a spinner on the Delete button + locks the dialog
 *   - confirmLabel: "Delete module" / "Delete subject" / "Delete course"
 *   - dataTestId: prefix for data-testid attrs
 */
export default function ConfirmDeleteDialog({
  open,
  onCancel,
  onConfirm,
  title = "Delete this item?",
  itemLabel,
  description = "This action cannot be undone.",
  busy = false,
  confirmLabel = "Delete",
  dataTestId = "confirm-delete",
}) {
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy && !v) onCancel?.(); }}>
      <DialogContent className="max-w-md" data-testid={`${dataTestId}-dialog`}>
        <DialogHeader>
          <div className="mx-auto mb-2 h-12 w-12 rounded-full bg-rose-100 dark:bg-rose-500/15 grid place-items-center">
            <AlertTriangle className="w-6 h-6 text-rose-600 dark:text-rose-400" />
          </div>
          <DialogTitle className="font-display text-center text-xl">{title}</DialogTitle>
          <DialogDescription className="text-center">
            {itemLabel && (
              <>
                <span className="font-semibold text-slate-900 dark:text-slate-100">"{itemLabel}"</span>{" "}
              </>
            )}
            {description}{" "}
            <span className="font-semibold text-rose-700 dark:text-rose-300">This action cannot be undone.</span>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2">
          <Button
            variant="outline"
            className="w-full sm:w-auto h-10"
            onClick={onCancel}
            disabled={busy}
            data-testid={`${dataTestId}-cancel-btn`}
          >
            Cancel
          </Button>
          <Button
            className="w-full sm:w-auto h-10 bg-rose-600 hover:bg-rose-700 text-white"
            disabled={busy}
            onClick={onConfirm}
            data-testid={`${dataTestId}-confirm-btn`}
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Trash2 className="w-4 h-4 mr-1.5" />}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
